<script setup lang="ts">
import { ref, computed } from 'vue';
import { useTranslation } from '@/utils/locales/messages';
import { useLanguageStore } from '@/shared/stores/languageStore';
import { apiClient } from '@/shared/api/client';
import { useDialog } from '@/shared/composables/useDialog';
import ControllerAppointmentForm from './ControllerAppointmentForm.vue';

// Dialog 초기화
const { warning: showWarning } = useDialog();

const languageStore = useLanguageStore();
const { createSyncT } = useTranslation();
const t = createSyncT(languageStore.currentLanguage);

// Props
interface Props {
  taskData?: any;
  taskId?: string;
  showBackButton?: boolean;
  readOnly?: boolean; // 읽기 전용 모드 (E-Filing 완료 상태)
}

const props = withDefaults(defineProps<Props>(), {
  taskData: null,
  taskId: '',
  showBackButton: true,
  readOnly: false
});

// Emits
const emit = defineEmits<{
  back: [];
  submit: [data: any];
  error: [error: any];
  'validation-success': [data: { eFilingData: any; entityUEN: string; filingAgentUEN: string; entityName: string }];
}>();

// ControllerAppointmentForm ref
const formRef = ref<InstanceType<typeof ControllerAppointmentForm> | null>(null);

// 상태
const isSubmitting = ref(false);
const isValidating = ref(false);

// 디버깅용 E-Filing 데이터 다이얼로그 (주석 처리)
// const showEFilingDataDialog = ref(false);
const debugData = ref<{
  originalEFilingData: any;
  mergedEFilingData: any;
  mergedContactData: any;
  mergedFormData: any;
  changes: Array<{ path: string; oldValue: any; newValue: any }>;
}>({
  originalEFilingData: null,
  mergedEFilingData: null,
  mergedContactData: null,
  mergedFormData: null,
  changes: []
});

// 검증 결과 (팝업 대신 화면 하단에 표시)
// const showValidationResult = ref(false); // 팝업 주석 처리
const validationResult = ref<any>(null);

// 필드명을 사용자 친화적으로 변환
const formatFieldLabel = (field: any): string => {
  // Code/Name 접미사 제거하여 깔끔하게 표시
  let label = field.label || field.field || '';

  // " Code", " Name" 접미사를 제거하고 하나로 통합
  if (label.endsWith(' Code') || label.endsWith(' Name')) {
    label = label.replace(/ Code$/, '').replace(/ Name$/, '');
  }

  return label;
};

// 필드별 validation 에러 매핑 (ControllerAppointmentForm에 전달)
const fieldValidationErrors = computed(() => {
  const fields = validationResult.value?.data?.missingFields || [];
  if (!fields.length) return {};

  const errors: Record<string, string> = {};
  
  fields.forEach((field: any) => {
    const fieldPath = field.field || '';
    const label = formatFieldLabel(field);
    
    // 필드 경로를 컴포넌트의 필드명으로 매핑
    const mappedField = mapFieldPathToComponentField(fieldPath);
    if (mappedField) {
      errors[mappedField] = t('startTask.This field is required') || `${label} is required`;
    }
  });

  return errors;
});

// 백엔드 필드 경로를 컴포넌트 필드명으로 매핑
const mapFieldPathToComponentField = (fieldPath: string): string | null => {
  // 필드 경로 예시: "entryDate", "birthDate", "postalCode", "entityType.code", "entityType.name" 등
  const fieldMap: Record<string, string> = {
    'entryDate': 'dateOfBecoming',
    'birthDate': 'dateOfBirth',
    'postalCode': 'postalCode',
    'foreignAddr1': 'foreignAddress1',
    'mobileNoCountryCode': 'mobileCountryCode',
    'mobileNo': 'mobileNumber',
    'entityType.code': 'entityType',
    'entityType.name': 'entityType',
    'entityName': 'entityName',
    'filingAgentUEN': 'filingAgentUEN',
    'entityUEN': 'entityUEN',
    // Contact 관련 필드
    'name': 'controllerName',
    'registrationIdNo': 'registrationIdNo',
    'idType.code': 'idType',
    'idType.name': 'idType',
    'nationality.code': 'nationality',
    'nationality.name': 'nationality',
    // Address 관련 필드
    'addressLine1': 'addressLine1',
    'addressLine2': 'addressLine2',
    'city': 'city',
    'country.code': 'country',
    'country.name': 'country',
  };

  // 정확한 매칭 먼저 시도
  if (fieldMap[fieldPath]) {
    return fieldMap[fieldPath];
  }

  // 점(.)으로 구분된 경로 처리 (예: "entityType.code" -> "entityType")
  const pathParts = fieldPath.split('.');
  if (pathParts.length > 1) {
    const baseField = pathParts[0];
    if (fieldMap[fieldPath]) {
      return fieldMap[fieldPath];
    }
    // baseField만 매핑 시도
    if (fieldMap[baseField]) {
      return fieldMap[baseField];
    }
  }

  // 매핑되지 않은 필드는 null 반환
  return null;
};

// 최종 전송용 데이터 (변환된 eFilingData)
const finalEFilingData = ref<any>(null);

// Entity 정보 (taskData 또는 finalEFilingData에서 추출)
const entityUEN = computed(() => {
  // 다양한 위치에서 entityUEN 추출 시도
  const fromEFilingData = props.taskData?.eFilingData?.entityUEN;
  const fromTaskData = props.taskData?.entityUEN;
  const fromFinalData = finalEFilingData.value?.entityUEN;
  // saveController의 첫 번째 항목에서 EntityUEN 추출
  const fromSaveController = props.taskData?.eFilingData?.saveController?.[0]?.EntityUEN ||
                          finalEFilingData.value?.saveController?.[0]?.EntityUEN;
  // entityType이 있는 경우 EntityUEN 필드명으로도 확인
  const fromEntityType = props.taskData?.eFilingData?.EntityUEN ||
                         finalEFilingData.value?.EntityUEN;

  const result = fromEFilingData || fromTaskData || fromFinalData || fromSaveController || fromEntityType || '';
  console.log('📋 [ControllerAppointmentEFiling] entityUEN computed:', {
    fromEFilingData,
    fromTaskData,
    fromFinalData,
    fromSaveController,
    fromEntityType,
    result,
    taskDataKeys: Object.keys(props.taskData || {}),
    eFilingDataKeys: Object.keys(props.taskData?.eFilingData || {})
  });
  return result;
});

const filingAgentUEN = computed(() => {
  // Filing Agent UEN이 별도로 있으면 사용, 없으면 entityUEN 사용
  const fromEFilingData = props.taskData?.eFilingData?.filingAgentUEN;
  const fromTaskData = props.taskData?.filingAgentUEN;
  const fromFinalData = finalEFilingData.value?.filingAgentUEN;
  // FilingAgentUEN 대문자 키로도 확인
  const fromUpperCase = props.taskData?.eFilingData?.FilingAgentUEN ||
                        finalEFilingData.value?.FilingAgentUEN;

  return fromEFilingData || fromTaskData || fromFinalData || fromUpperCase || entityUEN.value;
});

const entityName = computed(() => {
  return props.taskData?.eFilingData?.entityName?.name ||
         props.taskData?.eFilingData?.entityName ||
         props.taskData?.entityName ||
         finalEFilingData.value?.entityName ||
         '';
});

// { code, name } 객체를 name 값으로 변환하는 함수
const transformCodeNameToValue = (data: any): any => {
  if (data === null || data === undefined) {
    return data;
  }

  // 배열인 경우 각 요소를 재귀적으로 변환
  if (Array.isArray(data)) {
    return data.map(item => transformCodeNameToValue(item));
  }

  // 객체인 경우
  if (typeof data === 'object') {
    // { code, name } 형태인지 확인
    const keys = Object.keys(data);
    if (keys.length === 2 && keys.includes('code') && keys.includes('name')) {
      // name 값 반환 (name이 비어있으면 code 반환)
      return data.name || data.code || '';
    }

    // 일반 객체는 각 속성을 재귀적으로 변환
    const result: any = {};
    for (const key of keys) {
      result[key] = transformCodeNameToValue(data[key]);
    }
    return result;
  }

  // 기본형은 그대로 반환
  return data;
};

// 뒤로가기
const handleBack = () => {
  emit('back');
};

// Deep merge 함수 - 빈 값은 기존 값 유지
const deepMerge = (base: any, update: any): any => {
  if (!base) return update;
  if (!update) return base;

  // 둘 다 객체가 아니면 update 값 반환 (단, 빈 값이면 base 유지)
  if (typeof base !== 'object' || typeof update !== 'object') {
    // update가 빈 문자열이나 null/undefined면 base 유지
    if (update === '' || update === null || update === undefined) {
      return base;
    }
    return update;
  }

  // 배열인 경우
  if (Array.isArray(base) && Array.isArray(update)) {
    // 배열 요소별 deep merge
    const maxLen = Math.max(base.length, update.length);
    const result: any[] = [];
    for (let i = 0; i < maxLen; i++) {
      if (i < update.length && i < base.length) {
        result.push(deepMerge(base[i], update[i]));
      } else if (i < base.length) {
        result.push(base[i]);
      } else {
        result.push(update[i]);
      }
    }
    return result;
  }

  // 객체인 경우 - 모든 키에 대해 deep merge
  const result = { ...base };
  for (const key of Object.keys(update)) {
    const updateValue = update[key];
    const baseValue = base[key];

    // code/name 객체인 경우 특별 처리
    if (typeof updateValue === 'object' && updateValue !== null &&
        'code' in updateValue && 'name' in updateValue) {
      // code나 name이 비어있으면 base 값 유지
      if (!updateValue.code && !updateValue.name) {
        result[key] = baseValue || updateValue;
      } else if (!updateValue.code && baseValue?.code) {
        result[key] = { ...updateValue, code: baseValue.code };
      } else if (!updateValue.name && baseValue?.name) {
        result[key] = { ...updateValue, name: baseValue.name };
      } else {
        result[key] = deepMerge(baseValue, updateValue);
      }
    } else {
      result[key] = deepMerge(baseValue, updateValue);
    }
  }

  return result;
};

// task_data 기반으로 eFilingData 및 contactData 업데이트 (deep merge)
const mergeTaskDataWithEFiling = (baseTaskData: any, newEFilingData: any, newContactData?: any): any => {
  // 기본 task_data 복사
  const merged = JSON.parse(JSON.stringify(baseTaskData || {}));

  // formData가 없으면 생성
  if (!merged.formData) {
    merged.formData = {};
  }

  // 기존 eFilingData가 있으면 deep merge, 없으면 새로운 eFilingData 사용
  if (merged.eFilingData) {
    merged.eFilingData = deepMerge(merged.eFilingData, newEFilingData);
  } else {
    merged.eFilingData = newEFilingData;
  }

  // contactData가 있으면 업데이트 (현재 화면의 데이터로 덮어쓰기)
  if (newContactData) {
    merged.formData.contactData = newContactData;
  }

  return merged;
};

// Submit - E-Filing 데이터 생성 후 검증 및 저장
const handleSubmit = async () => {
  console.log('📋 [ControllerAppointmentEFiling] Submit 클릭');

  if (isSubmitting.value) return;

  try {
    isSubmitting.value = true;

    if (!formRef.value) {
      console.error('❌ [ControllerAppointmentEFiling] Form ref가 없습니다');
      return;
    }

    // ControllerAppointmentForm의 getEFilingData 호출 (Next 버튼과 동일한 규칙)
    const updatedEFilingData = await formRef.value.getEFilingData?.();
    console.log('📋 [ControllerAppointmentEFiling] Updated E-Filing 데이터:', updatedEFilingData);

    // ControllerAppointmentForm의 getContactData 호출 (현재 화면의 contactData 가져오기)
    const updatedContactData = formRef.value.getContactData?.();
    console.log('📋 [ControllerAppointmentEFiling] Updated Contact 데이터:', updatedContactData);

    // task_data 기반으로 eFilingData 및 contactData 업데이트
    const mergedData = mergeTaskDataWithEFiling(props.taskData, updatedEFilingData, updatedContactData);
    console.log('📋 [ControllerAppointmentEFiling] Merged 데이터:', mergedData);

    // 원본 eFilingData와 병합된 eFilingData
    const originalEFilingData = props.taskData?.eFilingData || null;
    const mergedEFilingData = mergedData.eFilingData || null;
    const mergedContactData = mergedData.formData?.contactData || null;

    // 디버깅용 다이얼로그 데이터 저장 (검증에 필요)
    debugData.value = {
      originalEFilingData,
      mergedEFilingData,
      mergedContactData, // contactData 추가
      mergedFormData: mergedData.formData, // formData 전체 저장
      changes: [] // 변경사항 추적은 주석 처리
    };

    // 바로 검증 및 저장 실행
    await handleValidateAndSubmit();
  } finally {
    isSubmitting.value = false;
  }
};

// 디버깅 다이얼로그에서 OK 클릭 - eFilingData 저장 후 /validate/:taskId 호출
const handleValidateAndSubmit = async () => {
  console.log('📋 [ControllerAppointmentEFiling] Save & Validate 호출');

  if (isValidating.value) return;

  try {
    isValidating.value = true;

    if (!props.taskId) {
      console.error('❌ [ControllerAppointmentEFiling] taskId가 없습니다');
      validationResult.value = {
        success: false,
        error: 'Task ID is required'
      };
      return;
    }

    // 프론트엔드 validation 먼저 체크 (백엔드 호출 전)
    if (formRef.value && typeof formRef.value.validateAll === 'function') {
      const isValid = await formRef.value.validateAll();
      if (!isValid) {
        console.log('❌ [ControllerAppointmentEFiling] 프론트엔드 validation 실패');
        // validateAll이 false를 반환하면 화면에 에러가 표시되므로 여기서 return
        // 모달을 띄우지 않고 필드에 에러 메시지가 표시됨
        return;
      }
    }

    // 프론트엔드 validation 통과 후 백엔드 API 호출
    // 1. eFilingData 및 contactData 저장 (update-efiling-data API 호출)
    console.log('📤 [ControllerAppointmentEFiling] eFilingData 및 contactData 저장 중...');
    
    // contactData 포함하여 저장
    const savePayload: any = { 
      eFilingData: debugData.value.mergedEFilingData 
    };
    
    // mergedData에서 contactData 가져오기
    if (debugData.value.mergedContactData) {
      savePayload.contactData = debugData.value.mergedContactData;
      console.log('📤 [ControllerAppointmentEFiling] contactData 포함:', savePayload.contactData);
    }
    
    const saveResponse = await apiClient.put(
      `/startTask/controller-appointment/update-efiling-data/${props.taskId}`,
      savePayload
    );
    console.log('✅ [ControllerAppointmentEFiling] eFilingData 및 contactData 저장 완료:', saveResponse);

    // 2. /validate/:taskId API 호출
    console.log('📤 [ControllerAppointmentEFiling] Validate 호출 중...');
    const response = await apiClient.get(`/startTask/controller-appointment/validate/${props.taskId}`);
    console.log('✅ [ControllerAppointmentEFiling] Validate 응답:', response);

    validationResult.value = {
      success: true,
      data: response?.data || response
    };

    // 검증 성공 시 최종 전송용 데이터 생성 (code/name -> name으로 변환)
    if (validationResult.value?.data?.isValid) {
      const transformedData = transformCodeNameToValue(debugData.value.mergedEFilingData);
      finalEFilingData.value = transformedData;
      console.log('📋 [ControllerAppointmentEFiling] 변환된 최종 데이터:', finalEFilingData.value);

      // 검증 성공 시 부모 컴포넌트(EFilingPage)로 데이터 전달하여 Singpass 모달 호출
      emit('validation-success', {
        eFilingData: finalEFilingData.value,
        entityUEN: entityUEN.value,
        filingAgentUEN: filingAgentUEN.value,
        entityName: entityName.value
      });
    } else {
      const missing = validationResult.value?.data?.missingFields || [];
      const missingSummary = missing.length
        ? missing.map((f: any) => f.label || f.field).filter(Boolean).join(', ')
        : '';
      const baseMsg =
        t('startTask.Please check the required fields and enter the values')
        || 'Please check the required fields and enter the values.';
      await showWarning(
        t('startTask.Validation Error') || 'Validation Error',
        missingSummary ? `${baseMsg}\n(${missingSummary})` : baseMsg,
      );
    }
  } catch (error: any) {
    console.error('❌ [ControllerAppointmentEFiling] Save 실패:', error);
    // 백엔드 에러 코드를 필드에 매핑하여 표시
    if (formRef.value && error.response?.data?.code) {
      const errorCode = error.response.data.code;
      const formComponent = formRef.value as any;
      if (formComponent.mapErrorCodeToField && formComponent.setFieldError) {
        const fieldName = formComponent.mapErrorCodeToField(errorCode);
        if (fieldName) {
          const errorMessage = error.response.data.message || error.message || 'This field is required';
          await formComponent.setFieldError(fieldName, errorMessage);
        }
      }
    }
    // 모달을 띄우지 않고 필드에 에러 메시지가 표시됨
  } finally {
    isValidating.value = false;
  }
};

// Form ref를 외부에서 접근 가능하도록 expose
defineExpose({
  formRef,
  getEFilingData: async () => {
    if (formRef.value) {
      return await formRef.value.getEFilingData?.();
    }
    return null;
  },
  getMergedData: async () => {
    if (formRef.value) {
      const updatedEFilingData = await formRef.value.getEFilingData?.();
      return mergeTaskDataWithEFiling(props.taskData, updatedEFilingData);
    }
    return null;
  }
});
</script>

<template>
  <v-card rounded="md" class="controller-appointment-efiling">
    <v-card-text>
      <ControllerAppointmentForm
        v-if="taskData"
        ref="formRef"
        :initial-data="taskData"
        :read-only="readOnly"
        :show-task-flow="false"
        :e-filing-mode="true"
        :validation-errors="fieldValidationErrors"
        :task-id="taskId"
      />
      <div v-else class="text-center py-8">
        <v-progress-circular indeterminate color="primary" size="48" />
        <p class="mt-4 text-grey">{{ t('startTask.Loading') || 'Loading...' }}</p>
      </div>
    </v-card-text>
    <v-divider v-if="!readOnly" />
    <v-card-actions v-if="!readOnly" class="justify-end pa-4">
      <v-btn
        v-if="showBackButton"
        variant="outlined"
        color="primary"
        :disabled="isSubmitting || isValidating"
        @click="handleBack"
      >
        {{ t('startTask.Back') || 'Back' }}
      </v-btn>
      <v-btn
        color="primary"
        variant="flat"
        :loading="isSubmitting || isValidating"
        :disabled="isSubmitting || isValidating"
        @click="handleSubmit"
      >
        {{ t('startTask.Submit') || 'Submit' }}
      </v-btn>
    </v-card-actions>
    <!-- 읽기 전용 모드일 때 완료 상태 표시 -->
    <v-divider v-if="readOnly" />
    <v-card-actions v-if="readOnly" class="justify-center pa-4">
      <v-alert type="success" variant="tonal" density="compact" class="mb-0">
        {{ t('startTask.E-Filing has been completed') || 'E-Filing has been completed' }}
      </v-alert>
    </v-card-actions>
  </v-card>
</template>

<style scoped>
.controller-appointment-efiling {
  margin: 16px;
  max-width: 100%;
  box-sizing: border-box;
}

.controller-appointment-efiling :deep(.v-card-text) {
  max-width: 100%;
  box-sizing: border-box;
}

/* v-row의 음수 마진으로 인한 가로 스크롤 방지 */
.controller-appointment-efiling :deep(.v-row) {
  margin-left: 0;
  margin-right: 0;
}

.controller-appointment-efiling :deep(.v-col) {
  padding-left: 8px;
  padding-right: 8px;
}
</style>

